/**
 * Centralised date formatting — DD MMM YY (e.g. "22 Oct 26").
 * Keep ISO in API payloads; format only at the presentation boundary.
 */

const DATE_OPTS: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: '2-digit',
}

const DATETIME_OPTS: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
}

function toDate(value: string | Date | null | undefined): Date | null {
  if (value == null || value === '') return null
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }
  // Date-only ISO (YYYY-MM-DD) → local calendar day (avoid UTC shift).
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (dateOnly) {
    const year = Number(dateOnly[1])
    const month = Number(dateOnly[2])
    const day = Number(dateOnly[3])
    const local = new Date(year, month - 1, day)
    return Number.isNaN(local.getTime()) ? null : local
  }
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/** Format a calendar date as "DD MMM YY" — e.g. "22 Oct 26". */
export function formatDate(value: string | Date | null | undefined, locale = 'en-GB'): string {
  const date = toDate(value)
  if (!date) return '—'
  try {
    return date.toLocaleDateString(locale, DATE_OPTS)
  } catch {
    return '—'
  }
}

/** Format a date-time as "DD MMM YY, HH:MM" — e.g. "22 Oct 26, 14:30". */
export function formatDateTime(value: string | Date | null | undefined, locale = 'en-GB'): string {
  const date = toDate(value)
  if (!date) return '—'
  try {
    return date.toLocaleString(locale, DATETIME_OPTS)
  } catch {
    return '—'
  }
}
